import { useDispatch } from 'store/hooks';
import { useCallback } from 'react';
import { UserInfoWithIdT } from 'business/definitions';
import { useSharingModalContext } from 'routes/Dashboard/components/SharingModalContext';
import { useTranslation } from 'react-i18next';
import { NestedMenuOptionT } from 'ui/NestedMenu/NestedMenu';
import { getPublicToken } from 'components/QueryPerimeter/hooks/useGetTreeData';
import { useParams } from 'react-router-dom';
import useGetDashboard from 'routes/Explore/hooks/useGetDashboard';

/**
 This function displays a snackbar to the user if one of the user mentionned doesn't have access to the current dashboard
 */
export const useWarnUserIfNoAccessRights = (): ((
    userIds: number[],
    users: UserInfoWithIdT[],
) => void) => {
    const { slug: slugParam } = useParams();
    const publicToken = getPublicToken();
    const { data: dashboard } = useGetDashboard(slugParam, publicToken);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { setIsSharingModalOpen } = useSharingModalContext();

    return useCallback(
        (userIds: number[], users: UserInfoWithIdT[]) => {
            if (
                !dashboard ||
                dashboard.orgSharing.isPublic ||
                dashboard.orgSharing.isRestricted
            )
                return;

            const everyoneHasAccess = userIds.every((userId) => {
                const user = users.find((u) => u.id === userId);

                return (
                    dashboard.usersSharing.some(
                        (userSharing) => userSharing.email === user?.email,
                    ) || userId === dashboard.ownerId
                );
            });

            if (!everyoneHasAccess) {
                dispatch.vision.notifyUser({
                    message: t('mention.no_access_rights'),
                    snackType: 'warning',
                    snackAction: {
                        message: t('mention.here'),
                        onClick: () => setIsSharingModalOpen(true),
                    },
                });
            }
        },
        [
            dashboard,
            dashboard?.orgSharing,
            dashboard?.usersSharing,
            dashboard?.ownerId,
        ],
    );
};

export const addInsertItem = (
    menuItems: NestedMenuOptionT[],
    insertChartItem: NestedMenuOptionT,
    canEdit: boolean,
    isExploreUser: boolean,
): NestedMenuOptionT[] => {
    return canEdit && isExploreUser
        ? [...menuItems, insertChartItem]
        : menuItems;
};
